import React, { useState } from 'react';
import '../healthoverview.css';
import { Card, CardContent, Typography } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import ChartControls from './ChartControls';
import { getActivitiesGraphData, getRecipientActivitiesGraphData } from '../../../utils/api';
import { useQuery, useQueryClient } from 'react-query';
import { fixedDecimalToTwo, getFormattedChartPeriodLabel } from '../../../utils';
import CustomTooltip from './CustomTooltip';
import { ACTIVITIES_CHART_TYPES, ACTIVITIES_CHART_LABELS } from '../../../constants';

export default function ActivityGraph({ userData, recipientId, type }) {
  const queryClient = useQueryClient();
  const userId = userData?._id;
  const [period, setPeriod] = useState('monthly');
  const [showComparison, setShowComparison] = useState(false);
  const [device, setDevice] = useState({
    _id: 'Average From All Devices',
    name: 'Average From All Devices',
  });
  const chartType = ACTIVITIES_CHART_TYPES[type];
  const deviceId = device._id === 'Average From All Devices' ? null : device._id;
  const { data: apiResponse } = useQuery({
    queryKey: ['activityGraph', userId, recipientId, chartType, period, showComparison, deviceId],
    queryFn: () =>
      recipientId
        ? getRecipientActivitiesGraphData({
            userId,
            recipientId,
            type: chartType,
            period,
            showComparison,
            deviceId, // Pass deviceId directly without any transformation
          })
        : getActivitiesGraphData({
            userId,
            type: chartType,
            period,
            showComparison,
            deviceId, // Pass deviceId directly without any transformation
          }),
    staleTime: 60000,
    retry: 2,
    refetchOnWindowFocus: false,
  });

  const getChartData = () => {
    if (!apiResponse?.data || apiResponse.data.length === 0) {
      return [{ period: 'No data available', current: 0, previous: 0 }];
    }
    return apiResponse.data.map((item) => {
      return {
        period: getFormattedChartPeriodLabel(period, item.period),
        current: fixedDecimalToTwo(item.data),
        previous: fixedDecimalToTwo(item.prevData),
      };
    });
  };

  const { label, unit } = ACTIVITIES_CHART_LABELS[chartType] || {};
  const chartData = getChartData();

  const getCurrentValues = () => {
    // Convert string values to numbers and filter out invalid values
    return chartData.map((item) => parseFloat(item.current)).filter((val) => !isNaN(val) && val > 0);
  };

  const chartValues = getCurrentValues();
  const avgValue = chartValues.length > 0 ? fixedDecimalToTwo(chartValues.reduce((sum, val) => sum + val, 0)) : 'N/A';
  const highestValue = chartValues.length > 0 ? Math.max(...chartValues) : 'N/A';
  const lowestValue = chartValues.length > 0 ? Math.min(...chartValues) : 'N/A';

  const handlePeriodChange = (event) => {
    setPeriod(event.target.value);
  };

  const toggleComparison = () => {
    setShowComparison(!showComparison);
  };

  const handleDeviceChange = (event) => {
    setDevice({
      _id: event._id,
      name: event?.name || 'Average From All Devices',
    });
    queryClient.invalidateQueries(['activityGraph', userId, recipientId, chartType, period, showComparison, event._id]);
  };

  return (
    <div className="heart-rate-container">
      <div className="header-section" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '30px' }}>
        <ChartControls
          period={period}
          handlePeriodChange={handlePeriodChange}
          showComparison={showComparison}
          toggleComparison={toggleComparison}
          dataType={type}
          userId={userId}
          selectedRecipient={recipientId}
          handleDeviceChange={handleDeviceChange}
          device={device}
        />
      </div>

      <Card className="stats-card">
        <CardContent className="stats-content">
          <div className="stat-item">
            <Typography color="textSecondary" className="stat-label" variant="caption">
              Total {label}
            </Typography>
            <Typography variant="h6" component="p" className="stat-value">
              {avgValue !== 'N/A' ? `${avgValue} ${unit}` : 'N/A'}
            </Typography>
          </div>
          <div className="stat-item">
            <Typography color="textSecondary" className="stat-label" variant="caption">
              Highest {label}
            </Typography>
            <Typography variant="subtitle1" component="p" className="stat-value">
              {highestValue !== 'N/A' ? `${highestValue} ${unit}` : 'N/A'}
            </Typography>
          </div>
          <div className="stat-item">
            <Typography color="textSecondary" className="stat-label" variant="caption">
              Lowest {label}
            </Typography>
            <Typography variant="subtitle1" component="p" className="stat-value">
              {lowestValue !== 'N/A' ? `${lowestValue} ${unit}` : 'N/A'}
            </Typography>
          </div>
        </CardContent>
      </Card>

      <div className="chart-container" style={{ height: '400px', width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 10, left: -20, bottom: 5 }}
            barSize={30}
            barGap={0}
            barCategoryGap={showComparison ? '15%' : '35%'}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
            <XAxis dataKey="period" axisLine={false} tickLine={false} tick={{ fill: '#888' }} />
            <YAxis domain={[0, 'auto']} axisLine={false} tickLine={false} tick={{ fill: '#888' }} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
            <ReferenceLine y={avgValue} stroke="#ddd" strokeDasharray="3 3" />

            {showComparison && (
              <Bar dataKey="previous" fill="#90EE90" radius={[4, 4, 0, 0]} animationDuration={1500} name="previous" />
            )}

            <Bar dataKey="current" fill="#006E5F" radius={[4, 4, 0, 0]} animationDuration={1500} name="current" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
